import React, { useRef, useState } from 'react';
import { UserCheck, Upload, FileCheck2, Save, Users } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { toast } from 'sonner';
import { upsertDriver } from '../lib/api';

/**
 * ItemDriverDetailsSection
 * ------------------------
 * Per-item driver / operator capture. Also lets users pick a previously-saved
 * driver from the Drivers Master to auto-fill every field (Iqama, License,
 * uploads), or save the currently entered driver back to the master with one
 * click.
 */
const ItemDriverDetailsSection = ({ item, index, onChange, savedDrivers = [], onDriverSaved }) => {
  const iqamaInputRef = useRef(null);
  const licenseInputRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const readAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleUpload = async (kind, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }
    try {
      const dataUrl = await readAsDataUrl(file);
      const key = kind === 'iqama' ? 'driver_iqama_url' : 'driver_license_url';
      const nameKey = kind === 'iqama' ? 'driver_iqama_filename' : 'driver_license_filename';
      onChange({ [key]: dataUrl, [nameKey]: file.name });
      toast.success(`${kind === 'iqama' ? 'Iqama' : 'Driving License'} uploaded`);
    } catch {
      toast.error('Failed to read file');
    }
  };

  const handlePickDriver = (d) => {
    onChange({
      driver_id: d.id,
      driver_name: d.name || '',
      driver_mobile: d.mobile || '',
      driver_iqama_number: d.iqama_number || '',
      driver_license_number: d.license_number || '',
      driver_iqama_url: d.iqama_url || '',
      driver_license_url: d.license_url || '',
      driver_iqama_filename: d.iqama_filename || '',
      driver_license_filename: d.license_filename || '',
    });
    setPickerOpen(false);
    toast.success(`Loaded driver: ${d.name}`);
  };

  const handleSaveToMaster = async () => {
    if (!item.driver_iqama_number || !item.driver_name) {
      toast.error('Driver name and Iqama number are required to save');
      return;
    }
    try {
      setSaving(true);
      const res = await upsertDriver({
        name: item.driver_name,
        mobile: item.driver_mobile,
        iqama_number: item.driver_iqama_number,
        license_number: item.driver_license_number,
        iqama_url: item.driver_iqama_url,
        license_url: item.driver_license_url,
        iqama_filename: item.driver_iqama_filename,
        license_filename: item.driver_license_filename,
      });
      onChange({ driver_id: res.data?.id });
      toast.success('Driver saved to master — pick it next time from the dropdown');
      if (typeof onDriverSaved === 'function') onDriverSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save driver');
    } finally {
      setSaving(false);
    }
  };

  const iqamaOk = !!item.driver_iqama_url;
  const licenseOk = !!item.driver_license_url;

  return (
    <div
      className="mt-2 rounded-md border border-blue-200 bg-blue-50/40 p-3"
      data-testid={`item-driver-section-${index}`}
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <UserCheck className="h-4 w-4 text-blue-700" />
        <span className="text-sm font-semibold text-blue-900">
          Driver / Operator for Item {index + 1}
        </span>
        <span className="text-[10px] text-blue-700 bg-blue-100 rounded-full px-2 py-0.5">
          Required before delivery
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                data-testid={`item-driver-picker-${index}`}
              >
                <Users className="h-3.5 w-3.5 mr-1" />
                Pick saved driver
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="end">
              <Command>
                <CommandInput placeholder="Search name, Iqama, mobile…" />
                <CommandList>
                  <CommandEmpty>No saved drivers yet.</CommandEmpty>
                  <CommandGroup>
                    {savedDrivers.slice(0, 50).map((d) => (
                      <CommandItem
                        key={d.id}
                        value={`${d.name} ${d.iqama_number} ${d.mobile}`}
                        onSelect={() => handlePickDriver(d)}
                        data-testid={`saved-driver-${d.id}`}
                        className="flex flex-col items-start gap-0.5"
                      >
                        <span className="font-medium">{d.name}</span>
                        <span className="text-[11px] text-muted-foreground">
                          Iqama {d.iqama_number} · {d.mobile || 'no mobile'}
                          {d.use_count ? ` · used ${d.use_count}×` : ''}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={handleSaveToMaster}
            disabled={saving || !item.driver_iqama_number || !item.driver_name}
            data-testid={`item-driver-save-${index}`}
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            {saving ? 'Saving…' : 'Save to master'}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Driver Name *</Label>
          <Input
            value={item.driver_name || ''}
            onChange={(e) => onChange({ driver_name: e.target.value })}
            placeholder="Full name"
            data-testid={`item-driver-name-${index}`}
          />
        </div>
        <div>
          <Label className="text-xs">Mobile Number *</Label>
          <Input
            type="tel"
            value={item.driver_mobile || ''}
            onChange={(e) => onChange({ driver_mobile: e.target.value })}
            placeholder="+9665..."
            data-testid={`item-driver-mobile-${index}`}
          />
        </div>
        <div>
          <Label className="text-xs">Iqama Number *</Label>
          <Input
            value={item.driver_iqama_number || ''}
            onChange={(e) => onChange({ driver_iqama_number: e.target.value })}
            placeholder="10-digit Iqama"
            data-testid={`item-driver-iqama-${index}`}
          />
        </div>
        <div>
          <Label className="text-xs">Driving License No. *</Label>
          <Input
            value={item.driver_license_number || ''}
            onChange={(e) => onChange({ driver_license_number: e.target.value })}
            data-testid={`item-driver-license-${index}`}
          />
        </div>
        <div>
          <Label className="text-xs">Iqama Copy *</Label>
          <input
            ref={iqamaInputRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => handleUpload('iqama', e)}
            data-testid={`item-driver-iqama-file-${index}`}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={`w-full ${iqamaOk ? 'border-emerald-400 text-emerald-800' : 'border-orange-300 text-orange-800'}`}
            onClick={() => iqamaInputRef.current?.click()}
            data-testid={`item-driver-iqama-upload-${index}`}
          >
            {iqamaOk ? <FileCheck2 className="h-4 w-4 mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
            {iqamaOk ? (item.driver_iqama_filename || 'Uploaded') : 'Upload Iqama Photo/PDF'}
          </Button>
        </div>
        <div>
          <Label className="text-xs">Driving License Copy *</Label>
          <input
            ref={licenseInputRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => handleUpload('license', e)}
            data-testid={`item-driver-license-file-${index}`}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={`w-full ${licenseOk ? 'border-emerald-400 text-emerald-800' : 'border-orange-300 text-orange-800'}`}
            onClick={() => licenseInputRef.current?.click()}
            data-testid={`item-driver-license-upload-${index}`}
          >
            {licenseOk ? <FileCheck2 className="h-4 w-4 mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
            {licenseOk ? (item.driver_license_filename || 'Uploaded') : 'Upload License Photo/PDF'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ItemDriverDetailsSection;
